// src/HRACalculator.js
import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import 'tailwindcss/tailwind.css'; // Make sure you have tailwindcss installed and imported
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import AdBanner from '../AdPage/AdBanner';

// Register necessary components
ChartJS.register(ArcElement, Tooltip, Legend);

const HRACalculator = () => {
  const [basicSalary, setBasicSalary] = useState(540050);
  const [dearnessAllowance, setDearnessAllowance] = useState(0);
  const [hraReceived, setHraReceived] = useState(100000);
  const [totalRentPaid, setTotalRentPaid] = useState(300000);
  const [isMetroCity, setIsMetroCity] = useState(false);
  const [exemptedHRA, setExemptedHRA] = useState(0);
  const [taxableHRA, setTaxableHRA] = useState(0);

  const calculateHRA = () => {
    const actualHRAReceived = hraReceived;
    const rentMinus10PercentSalary = totalRentPaid - (0.1 * (basicSalary + dearnessAllowance));
    const metroCityHRA = isMetroCity ? 0.5 * basicSalary : 0.4 * basicSalary;

    const hraExempt = Math.min(actualHRAReceived, rentMinus10PercentSalary, metroCityHRA);
    const hraTaxable = actualHRAReceived - hraExempt;

    setExemptedHRA(hraExempt);
    setTaxableHRA(hraTaxable);
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value === '' ? 0 : parseInt(e.target.value));
  };

  const handleMetroCityChange = (e) => {
    setIsMetroCity(e.target.value === 'yes');
  };

  const data = {
    labels: ['Exempted HRA', 'Taxable HRA'],
    datasets: [
      {
        label: 'HRA Breakdown',
        data: [exemptedHRA, taxableHRA],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is an HRA calculator?',
        answer: 'An HRA calculator is an online tool that helps individuals estimate the tax exemption they can claim on their House Rent Allowance.',
      },
      {
        question: 'Is HRA calculator free to use?',
        answer: 'Yes, most online HRA calculators are free and accessible to all users.',
      },
      {
        question: 'What details do I need to use an HRA calculator?',
        answer: 'You need to input your basic salary, HRA received, rent paid, and city of residence.',
      },
      {
        question: 'How accurate is an HRA calculator?',
        answer: 'An HRA calculator is highly accurate as long as the input details provided are correct.',
      },
      {
        question: 'Can I use HRA calculator for tax planning?',
        answer: 'Yes, it helps you understand the possible tax exemption and plan your salary structure accordingly.',
      },
      {
        question: 'Does the city of residence impact the HRA exemption?',
        answer: 'Yes, the exemption is 50% of basic salary for metro cities and 40% for non-metro cities.',
      },
      {
        question: 'Is rent receipt mandatory to claim HRA exemption?',
        answer: 'Yes, especially if your annual rent exceeds Rs 1,00,000.',
      },
      {
        question: 'What happens if I live in my own house?',
        answer: 'You cannot claim HRA exemption if you live in a self-owned house.',
      },
      {
        question: 'Can I claim HRA and home loan benefits together?',
        answer: 'Yes, if your own house is in a different city and you live on rent elsewhere.',
      },
      {
        question: 'What is considered a metro city for HRA?',
        answer: 'Delhi, Mumbai, Kolkata, and Chennai are considered metro cities.',
      },
      {
        question: 'Can I claim HRA if I live with my parents?',
        answer: 'Yes, if you pay rent to them and have proper rent receipts and agreement.',
      },
      {
        question: 'How often should I use an HRA calculator?',
        answer: 'You can use it annually or whenever your salary or rent changes.',
      },
      {
        question: 'What if I change cities mid-year?',
        answer: 'Your HRA exemption will be calculated proportionately based on the time spent in each city.',
      },
      {
        question: 'Can self-employed individuals claim HRA?',
        answer: 'No, HRA is only available to salaried individuals receiving HRA from their employer.',
      },
      {
        question: 'Where do I declare HRA while filing taxes?',
        answer: 'You can declare HRA in the ‘Income from Salary’ section under deductions when filing ITR.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
         <title>HRA Calculator – House Rent Allowance Tax Exemption</title>
        <meta  name='description' content='Use our HRA calculator to determine how much House Rent Allowance you can claim as tax exemption under income tax rules.'/>
        <meta name='keywords' content='hra exemption calculator, calculate hra tax exemption, calculate hra deduction, calculation of hra rebate, computation of hra exemption, hra calculation for tax exemption' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/hra-exemption-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">HRA Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
       <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
    <div className='py-4'>
      <div className="container mx-auto p-4 shadow-xl rounded-md">
        <h1 className="text-2xl font-bold mb-4">HRA Calculator</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Basic Salary (p.a)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={basicSalary}
                  onChange={handleInputChange(setBasicSalary)}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {basicSalary.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="10000000"
                value={basicSalary}
                onChange={(e) => setBasicSalary(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Dearness Allowance (p.a)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={dearnessAllowance}
                  onChange={handleInputChange(setDearnessAllowance)}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {dearnessAllowance.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1000000"
                value={dearnessAllowance}
                onChange={(e) => setDearnessAllowance(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">HRA Received (p.a)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={hraReceived}
                  onChange={handleInputChange(setHraReceived)}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {hraReceived.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="500000"
                value={hraReceived}
                onChange={(e) => setHraReceived(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Total Rent Paid (p.a)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  value={totalRentPaid}
                  onChange={handleInputChange(setTotalRentPaid)}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {totalRentPaid.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1000000"
                value={totalRentPaid}
                onChange={(e) => setTotalRentPaid(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Are you working in a metro city?</label>
              <div className="flex items-center">
                <label className="mr-4">
                  <input
                    type="radio"
                    value="yes"
                    checked={isMetroCity === true}
                    onChange={handleMetroCityChange}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    value="no"
                    checked={isMetroCity === false}
                    onChange={handleMetroCityChange}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
            </div>
            <button
              onClick={calculateHRA}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Calculate
            </button>
          </div>
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-bold">Results</h2>
              <p>Exempted HRA: ₹ {exemptedHRA.toLocaleString()}</p>
              <p>Taxable HRA: ₹ {taxableHRA.toLocaleString()}</p>
            </div>
            <div>
              <h2 className="text-xl font-bold">Breakdown</h2>
              <div className="flex justify-center">
                <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Understanding House Rent Allowance (HRA) and Tax Benefits
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            House Rent Allowance (HRA) is a key component of the salary structure for most salaried individuals in India. Designed to support employees in managing their rental expenses, HRA also offers significant tax-saving opportunities under Section 10(13A) of the Income Tax Act. By understanding how HRA works and using a smart tool like an HRA calculator, individuals can plan their taxes efficiently and reduce their annual tax burden.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is House Rent Allowance (HRA)?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              HRA is an allowance paid by employers to employees to help them meet the cost of living in rented accommodation. It is applicable only when the employee is living in a rented house. If you are staying in your own home, the HRA received becomes fully taxable.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The amount of HRA paid depends on multiple factors including the city of residence, the salary structure, and whether the accommodation is rented or self-owned. For cities classified as metros (like Mumbai, Delhi, Kolkata, Chennai), the HRA component tends to be higher due to elevated living costs.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Can Claim HRA Exemption?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To claim an exemption on HRA under the Income Tax Act, the following conditions must be met:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Salaried Employees:</strong> Only salaried individuals receiving HRA as part of their salary can claim the exemption.</li>
              <li><strong>Living in Rented Accommodation:</strong> You must be paying rent for your residence.</li>
              <li><strong>Proper Documentation:</strong> Rent receipts or rental agreements must be maintained as proof of rent paid.</li>
              <li><strong>PAN of Landlord:</strong> If annual rent exceeds Rs 1,00,000, submission of landlord's PAN is mandatory.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This exemption is not available to self-employed individuals or to those who do not receive HRA.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is HRA Tax Exemption Calculated?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The exempted amount under HRA is the least of the following three:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Actual HRA received from the employer</li>
              <li>50% of basic salary for metro cities or 40% for non-metro cities</li>
              <li>Actual rent paid minus 10% of basic salary</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Basic Salary: Rs 40,000/month</li>
              <li>HRA received: Rs 20,000/month</li>
              <li>Rent paid: Rs 15,000/month</li>
              <li>City: Non-metro</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Exemption will be least of:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Actual HRA received = Rs 20,000</li>
              <li>40% of Basic Salary = Rs 16,000</li>
              <li>Rent paid – 10% of Basic = Rs 15,000 – Rs 4,000 = Rs 11,000</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              So, HRA Exempt = Rs 11,000
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use an HRA Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Manual HRA calculations can be time-consuming and error-prone. An HRA calculator automates this process and provides an accurate estimation of your tax exemption. With inputs like basic salary, HRA received, rent paid, and city of residence, the tool instantly provides your eligible HRA tax exemption.
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Documents Required to Claim HRA
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To claim HRA exemption, you’ll need:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Rent receipts (for each month of the financial year)</li>
              <li>PAN of landlord (if annual rent exceeds ₹1 lakh)</li>
              <li>Rental agreement (optional but recommended)</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using an HRA Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Quick Results:</strong> Instantly calculates your HRA exemption without complicated manual formulas.</li>
              <li><strong>Helps in Tax Planning:</strong> Understand how much HRA you can claim and adjust your salary structure or rent accordingly.</li>
              <li><strong>Transparent Planning:</strong> Useful during salary negotiations and yearly investment declarations.</li>
              <li><strong>Reduces Errors:</strong> Minimizes chances of miscalculations or incorrect declarations to HR or IT departments.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              HRA Calculator Inputs and Outputs
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              When using an HRA calculator, you’ll typically need to enter:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Basic Monthly Salary</li>
              <li>HRA Received</li>
              <li>Monthly Rent Paid</li>
              <li>City of Residence</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The output will be the HRA exemption amount you can claim under Section 10(13A), which helps reduce your taxable income.
            </p>
          </div>

          {/* <h2 className="text-xl font-medium text-gray-700 mt-4">
            “Maximize your tax savings with TheFinMaster! Use our HRA Calculator to plan your House Rent Allowance exemptions effortlessly!”
          </h2> */}
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>


                         {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>
      
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>

  );
};

export default HRACalculator;
